import { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import FeedbackButton from '@/components/FeedbackButton';
import IOSInstallBanner from '@/components/IOSInstallBanner';
import { CartProvider } from '@/context/CartContext';
import { captureRefFromUrl } from '@/utils/egerieRef';

// Code-split each page so the initial bundle stays small (mobile-friendly).
const Marketplace = lazy(() => import('@/pages/Marketplace'));
const CustomerHome = lazy(() => import('@/pages/CustomerHome'));
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const BrowseProviders = lazy(() => import('@/pages/BrowseProviders'));
const ProviderProfile = lazy(() => import('@/pages/ProviderProfile'));
const BrowseRentals = lazy(() => import('@/pages/BrowseRentals'));
const BrowsePropertySales = lazy(() => import('@/pages/BrowsePropertySales'));
const RentalDetail = lazy(() => import('@/pages/RentalDetail'));
const ShopDetail = lazy(() => import('@/pages/ShopDetail'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Cart = lazy(() => import('@/pages/Cart'));
const CustomerAuth = lazy(() => import('@/pages/CustomerAuth'));
const CustomerDashboard = lazy(() => import('@/pages/CustomerDashboard'));
import EgeriesListPage from '@/pages/EgeriesListPage';
const EgerieAuthPage = lazy(() => import('@/pages/EgerieAuthPage'));
const EgerieDashboardPage = lazy(() => import('@/pages/EgerieDashboardPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const AdminAuth = lazy(() => import('@/pages/AdminAuth'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const CompanyAuth = lazy(() => import('@/pages/CompanyAuth'));
const CompanyDashboard = lazy(() => import('@/pages/CompanyDashboard'));
const VerifyPhonePage = lazy(() => import('@/pages/VerifyPhonePage'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-slate-500">Chargement…</p>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCustomerAuthenticated, setIsCustomerAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isCompanyAuthenticated, setIsCompanyAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const customerToken = localStorage.getItem('customerToken');
    const adminToken = localStorage.getItem('adminToken');
    const companyToken = localStorage.getItem('companyToken');
    setIsAuthenticated(!!token);
    setIsCustomerAuthenticated(!!customerToken);
    setIsAdminAuthenticated(!!adminToken);
    setIsCompanyAuthenticated(!!companyToken);
    // Capture ?ref=egerie_id share links (fire once on app load)
    captureRefFromUrl();
    setLoading(false);
  }, []);

  if (loading) {
    return <PageFallback />;
  }

  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Marketplace isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/home-classic" element={<CustomerHome isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/old-landing" element={<LandingPage isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/browse" element={<BrowseProviders isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/provider/:providerId" element={<ProviderProfile isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/rentals" element={<BrowseRentals isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/property-sales" element={<BrowsePropertySales isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/rental/:rentalId" element={<RentalDetail />} />

            {/* Makiti Routes */}
            <Route path="/makiti" element={<Marketplace isCustomerAuthenticated={isCustomerAuthenticated} />} />
            <Route path="/makiti/shop/:shopId" element={<ShopDetail />} />
            <Route path="/makiti/product/:productId" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />

            {/* Customer Routes */}
            <Route
              path="/customer/auth"
              element={isCustomerAuthenticated ? <Navigate to="/customer/dashboard" /> : <CustomerAuth setIsCustomerAuthenticated={setIsCustomerAuthenticated} />}
            />
            <Route
              path="/customer/dashboard"
              element={isCustomerAuthenticated ? <CustomerDashboard setIsCustomerAuthenticated={setIsCustomerAuthenticated} /> : <Navigate to="/customer/auth" />}
            />

            {/* Provider Routes */}
            <Route
              path="/auth"
              element={isAuthenticated ? <Navigate to="/dashboard" /> : <AuthPage setIsAuthenticated={setIsAuthenticated} />}
            />
            <Route
              path="/dashboard"
              element={isAuthenticated ? <Dashboard setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/auth" />}
            />

            {/* Company Routes */}
            <Route
              path="/company/auth"
              element={isCompanyAuthenticated ? <Navigate to="/company/dashboard" /> : <CompanyAuth setIsCompanyAuthenticated={setIsCompanyAuthenticated} />}
            />
            <Route
              path="/company/dashboard"
              element={isCompanyAuthenticated ? <CompanyDashboard setIsCompanyAuthenticated={setIsCompanyAuthenticated} /> : <Navigate to="/company/auth" />}
            />

            {/* Phone verification */}
            <Route path="/verify-phone" element={<VerifyPhonePage />} />

            {/* Égérie (brand ambassador) Routes */}
            <Route path="/egeries" element={<EgeriesListPage />} />
            <Route path="/egerie/auth" element={<EgerieAuthPage />} />
            <Route path="/egerie/dashboard" element={<EgerieDashboardPage />} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={isAdminAuthenticated ? <Navigate to="/admin/dashboard" /> : <AdminAuth setIsAdminAuthenticated={setIsAdminAuthenticated} />}
            />
            <Route
              path="/admin/dashboard"
              element={isAdminAuthenticated ? <AdminDashboard setIsAdminAuthenticated={setIsAdminAuthenticated} /> : <Navigate to="/admin" />}
            />
          </Routes>
        </Suspense>
        </BrowserRouter>
      </CartProvider>
      <Toaster />
      <FeedbackButton />
      <IOSInstallBanner />
    </div>
  );
}

export default App;
